import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Loader2, Download, Activity, AlertOctagon, Timer, ArrowDownUp,
} from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend, BarChart,
} from "recharts";

const RANGES = [
  { id: "24h", label: "24h" },
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
];

const METHODS = ["all", "GET", "POST", "PATCH", "DELETE"];
const STATUS = ["all", "2xx", "4xx", "5xx"];

function fmt(n) {
  if (n === null || n === undefined) return "—";
  if (typeof n === "number" && n >= 1000) return n.toLocaleString();
  return String(n);
}

function fmtBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = Number(n);
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function fmtBucket(iso, granularity) {
  const d = new Date(iso);
  if (granularity === "day") {
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  }
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * UsageDashboard — reusable shell for tenant + master usage pages.
 *
 * Props:
 *  - basePath: API base used to fetch the usage payload.
 *    E.g. `/tenant/api-keys/{id}` or `/master/api-keys/{id}`.
 *  - variant: "tenant" | "master"
 *  - keyId: uuid
 *  - onBack: () => void (renders a Back button when supplied)
 */
export default function UsageDashboard({ basePath, variant = "tenant", keyId, onBack }) {
  const [range, setRange] = useState("24h");
  const [method, setMethod] = useState("all");
  const [statusClass, setStatusClass] = useState("all");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = { range };
      if (method !== "all") params.method = method;
      if (statusClass !== "all") params.status_class = statusClass;
      const { data } = await api.get(`${basePath}/usage`, { params });
      setData(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range, method, statusClass]);

  const downloadCsv = async () => {
    try {
      const params = { range };
      if (method !== "all") params.method = method;
      if (statusClass !== "all") params.status_class = statusClass;
      const resp = await api.get(`${basePath}/usage.csv`, { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `usage-${keyId}-${range}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const chartRows = useMemo(() => {
    if (!data?.series) return [];
    return data.series.map((s) => ({
      bucket: fmtBucket(s.bucket_start, data.granularity),
      success: Math.max(0, s.requests - s.errors),
      errors: s.errors,
      p95: s.p95,
    }));
  }, [data]);

  return (
    <div className="space-y-6" data-testid="usage-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          {onBack && (
            <Button
              variant="ghost" size="sm" onClick={onBack} className="mb-2 -ml-3"
              data-testid="usage-back-btn"
            >
              ← Back
            </Button>
          )}
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
            {variant === "master" ? "Platform admin · API usage" : "Usage"}
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary" data-testid="usage-key-name">
            {data?.key_name || "…"}
          </h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              {data?.key_prefix || ""}
            </code>
            <span>·</span>
            <span data-testid="usage-last-used">
              {data?.last_used_at
                ? `Last used ${new Date(data.last_used_at).toLocaleString([], {
                    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                  })}`
                : "Never used"}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ToggleGroup
            type="single"
            value={range}
            onValueChange={(v) => v && setRange(v)}
            data-testid="usage-range-toggle"
          >
            {RANGES.map((r) => (
              <ToggleGroupItem key={r.id} value={r.id} data-testid={`usage-range-${r.id}`}>
                {r.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadCsv}
            data-testid="usage-csv-export"
          >
            <Download className="mr-1 h-4 w-4" /> CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Method</span>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="w-28" data-testid="usage-method-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {METHODS.map((m) => (
                <SelectItem key={m} value={m} data-testid={`usage-method-${m}`}>
                  {m === "all" ? "All" : m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Status</span>
          <Select value={statusClass} onValueChange={setStatusClass}>
            <SelectTrigger className="w-24" data-testid="usage-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS.map((s) => (
                <SelectItem key={s} value={s} data-testid={`usage-status-${s}`}>
                  {s === "all" ? "All" : s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-16" data-testid="usage-loading">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && error && (
        <Card className="border-destructive/40">
          <CardContent className="py-8 text-center text-destructive" data-testid="usage-error">
            {error}
          </CardContent>
        </Card>
      )}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="usage-kpis">
            <KpiCard
              icon={<Activity className="h-4 w-4" />}
              label="Total requests"
              value={fmt(data.total_requests)}
              testid="usage-kpi-total"
            />
            <KpiCard
              icon={<AlertOctagon className="h-4 w-4" />}
              label="Error rate"
              value={`${(data.error_rate * 100).toFixed(2)}%`}
              subtle={`${fmt(data.total_errors)} of ${fmt(data.total_requests)}`}
              testid="usage-kpi-error-rate"
            />
            <KpiCard
              icon={<Timer className="h-4 w-4" />}
              label="p95 latency"
              value={`${Math.round(data.p95_ms)} ms`}
              subtle={`p50 ${Math.round(data.p50_ms)} · p99 ${Math.round(data.p99_ms)}`}
              testid="usage-kpi-p95"
            />
            <KpiCard
              icon={<ArrowDownUp className="h-4 w-4" />}
              label="Bytes out"
              value={fmtBytes(data.bytes_out_total)}
              subtle={`in ${fmtBytes(data.bytes_in_total)}`}
              testid="usage-kpi-bytes"
            />
          </div>

          {data.total_requests === 0 && (
            <Card>
              <CardContent className="py-14 text-center text-muted-foreground" data-testid="usage-empty-state">
                <div className="text-sm font-medium">No traffic yet in this window.</div>
                <div className="mt-1 text-xs">
                  Once this key hits <code className="rounded bg-muted px-1">/api/v1/*</code>, the numbers show up here.
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Requests over time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72" data-testid="usage-chart-requests">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartRows} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="bucket" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8, fontSize: 12,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="success" name="Success" stackId="req" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="errors" name="Errors" stackId="req" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} />
                    <Line
                      dataKey="p95" name="p95 ms" type="monotone"
                      stroke="hsl(var(--secondary))" strokeWidth={2} dot={false} yAxisId={0}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top endpoints</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64" data-testid="usage-chart-endpoints">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={(data.top_endpoints || []).slice(0, 10)}
                      layout="vertical"
                      margin={{ top: 5, right: 20, left: 5, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                      <YAxis
                        type="category" dataKey="path_template" width={190}
                        stroke="hsl(var(--muted-foreground))" fontSize={11}
                        tickFormatter={(v) => (v.length > 26 ? `…${v.slice(-25)}` : v)}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "hsl(var(--popover))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 8, fontSize: 12,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="count" name="Requests" stackId="e" fill="hsl(var(--primary))" />
                      <Bar dataKey="error_count" name="Errors" stackId="e" fill="hsl(var(--destructive))" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top error codes</CardTitle>
              </CardHeader>
              <CardContent className="p-0" data-testid="usage-error-codes">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.top_error_codes || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} className="py-8 text-center text-muted-foreground">
                          No errors in this window.
                        </TableCell>
                      </TableRow>
                    )}
                    {(data.top_error_codes || []).map((e) => (
                      <TableRow key={e.status_code}>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              e.status_code >= 500
                                ? "border-destructive/40 text-destructive"
                                : "border-amber-500/40 text-amber-700"
                            }
                          >
                            {e.status_code}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{e.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, subtle, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
          {icon}
          {label}
        </div>
        <div className="mt-2 font-display text-2xl font-semibold text-primary tabular-nums">
          {value}
        </div>
        {subtle && (
          <div className="mt-1 text-xs text-muted-foreground">{subtle}</div>
        )}
      </CardContent>
    </Card>
  );
}
